import { useCallback } from 'react';
import { SweetAlertService } from '../services/alert/sweetAlertService';
import type { SweetAlertIcon } from 'sweetalert2';

export const useSweetAlert = () => {
    // Blocking versions
    const showSuccess = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        return SweetAlertService.success(title, text, callback);
    }, []);

    const showError = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        return SweetAlertService.error(title, text, callback);
    }, []);

    const showWarning = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        return SweetAlertService.warning(title, text, callback);
    }, []);

    const showInfo = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        return SweetAlertService.info(title, text, callback);
    }, []);

    // Non-blocking versions
    const showSuccessAsync = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        SweetAlertService.successAsync(title, text, callback);
    }, []);

    const showErrorAsync = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        SweetAlertService.errorAsync(title, text, callback);
    }, []);

    const showWarningAsync = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        SweetAlertService.warningAsync(title, text, callback);
    }, []);

    const showInfoAsync = useCallback((title: string, text?: string, callback?: () => Promise<void> | void) => {
        SweetAlertService.infoAsync(title, text, callback);
    }, []);

    // Confirm dialogs
    const showConfirm = useCallback((
        title: string,
        text?: string,
        confirmButtonText = 'Co',
        cancelButtonText = 'Khong',
        callback?: () => Promise<void> | void
    ) => {
        return SweetAlertService.confirm(title, text, callback, confirmButtonText, cancelButtonText);
    }, []);

    const showConfirmAsync = useCallback((
        title: string,
        text?: string,
        confirmButtonText = 'Co',
        cancelButtonText = 'Khong',
        callback?: (confirmed: boolean) => Promise<void> | void
    ) => {
        SweetAlertService.confirmAsync(title, text, callback, confirmButtonText, cancelButtonText);
    }, []);

    const showConfirmDelete = useCallback((title?: string, text?: string, callback?: () => Promise<void> | void) => {
        return SweetAlertService.confirmDelete(title, text, callback);
    }, []);

    // Toast notifications
    const showToast = useCallback((icon: SweetAlertIcon, title: string, timer?: number, callback?: () => Promise<void> | void) => {
        SweetAlertService.toast(icon, title, timer, callback);
    }, []);

    // Loading dialogs
    const showLoading = useCallback((title?: string, text?: string) => {
        SweetAlertService.showLoading(title, text);
    }, []);

    const hideLoading = useCallback(() => {
        SweetAlertService.hideLoading();
    }, []);

    const showProgress = useCallback((title?: string, text?: string, initialPercent?: number) => {
        SweetAlertService.showProgress(title, text, initialPercent);
    }, []);

    const updateProgress = useCallback((percent: number, text?: string, status?: 'active' | 'success' | 'exception') => {
        SweetAlertService.updateProgress(percent, text, status);
    }, []);

    const showLoadingWithProgress = useCallback((
        title?: string,
        progressCallback?: (progress: number) => Promise<void> | void
    ) => {
        return SweetAlertService.showLoadingWithProgress(title, progressCallback);
    }, []);

    // Input dialogs
    const showInput = useCallback((
        title: string,
        inputType: 'text' | 'email' | 'password' | 'number' | 'textarea' = 'text',
        placeholder?: string,
        defaultValue?: string,
        callback?: (value: string) => Promise<void> | void
    ) => {
        return SweetAlertService.input(title, inputType, placeholder, defaultValue, callback);
    }, []);

    const showSelect = useCallback((
        title: string,
        options: Record<string, string>,
        placeholder?: string,
        callback?: (value: string) => Promise<void> | void
    ) => {
        return SweetAlertService.select(title, options, placeholder, callback);
    }, []);

    // Advanced features
    const showMultiStepConfirm = useCallback((
        steps: Array<{
            title: string;
            text?: string;
            icon?: SweetAlertIcon;
        }>,
        callback?: () => Promise<void> | void
    ) => {
        return SweetAlertService.multiStepConfirm(steps, callback);
    }, []);

    const showErrorWithRetry = useCallback((
        title: string,
        text?: string,
        retryCallback?: () => Promise<void> | void
    ) => {
        return SweetAlertService.errorWithRetry(title, text, retryCallback);
    }, []);

    const showHandleErrorApi = useCallback((
        options?: Parameters<typeof SweetAlertService.showHandleErrorApi>[0],
    ) => {
        return SweetAlertService.showHandleErrorApi(options);
    }, []);

    const showHandleErrorApiAsync = useCallback((
        options?: Parameters<typeof SweetAlertService.showHandleErrorApiAsync>[0],
    ) => {
        SweetAlertService.showHandleErrorApiAsync(options);
    }, []);

    return {
        // Blocking versions
        showSuccess,
        showError,
        showWarning,
        showInfo,

        // Non-blocking versions
        showSuccessAsync,
        showErrorAsync,
        showWarningAsync,
        showInfoAsync,

        // Confirm dialogs
        showConfirm,
        showConfirmAsync,
        showConfirmDelete,

        // Toast notifications
        showToast,

        // Loading dialogs
        showLoading,
        hideLoading,
        showProgress,
        updateProgress,
        showLoadingWithProgress,

        // Input dialogs
        showInput,
        showSelect,

        // Advanced features
        showMultiStepConfirm,
        showErrorWithRetry,
        showHandleErrorApi,
        showHandleErrorApiAsync,
    };
};